<template>
  <div class="custom-page">
    <div class="workspace-toolbar">
      <div><h2>自由工作台</h2><p>把常用工具放在一起，按你的习惯安排顺序</p></div>
      <div class="workspace-actions"><button class="btn-save" @click="panelOpen = true">添加组件</button><button class="btn-action" @click="saveLayout">保存布局</button></div>
    </div>
    <div v-if="panelOpen" class="component-mask" @click="panelOpen = false"></div>
    <div class="page-content">
      <aside class="component-panel" :class="{ 'is-open': panelOpen }">
        <div class="panel-header">
          <h3 class="panel-title">组件列表</h3><button class="panel-close" aria-label="关闭组件列表" @click="panelOpen = false">×</button>
        </div>
        <div class="panel-body">
          <div v-for="(widgets, category) in categorizedWidgets" :key="category" class="category-section">
            <div class="category-header" @click="toggleCategory(category)">
              <h3 class="category-title">{{ category }}</h3>
              <app-icon  class="app-icon category-arrow" :class="{ collapsed: collapsedCategories[category] }" name="expand_more"></app-icon>
            </div>
            <div class="component-list" v-show="!collapsedCategories[category]">
              <div class="component-item" v-for="item in widgets" :key="item.id" @click="addWidget(item)"
                :draggable="!isMobile" @dragstart="dragStart($event, item)">
                <app-icon  class="app-icon" :name="item.icon"></app-icon>
                <span class="component-name">{{ item.name }}</span>
              </div>
            </div>
          </div>
        </div>
        <div class="panel-footer">
          <button class="btn-action" @click="clearCanvas">
            <app-icon  class="app-icon" name="delete_sweep"></app-icon>
            清空
          </button>
          <button class="btn-save" @click="saveLayout">
            <app-icon  class="app-icon" name="save"></app-icon>
            保存
          </button>
        </div>
      </aside>

      <main class="canvas-area">
        <div class="canvas" @dragover.prevent @drop="handleDrop">
          <div v-if="canvasWidgets.length === 0" class="canvas-empty">
            <app-icon  class="app-icon" name="dashboard_customize"></app-icon>
            <h3>自定义您的工作台</h3>
            <p>{{ isMobile ? '点击添加组件，开始搭建工作台' : '从左侧选择组件添加到画布' }}</p>
          </div>
          <div v-else class="masonry" ref="masonry" :style="{ minHeight: masonryHeight + 'px' }">
            <div v-for="(item, index) in canvasWidgets" :key="item.id" class="masonry-item"
              :style="layoutCache[item.id] || {}"
              @dragover.prevent @drop="onItemDrop($event, index)">
              <div v-if="isMobile" class="widget-order"><span>{{ item.name }}</span><button :disabled="index === 0" @click="moveWidget(index, -1)">上移</button><button :disabled="index === canvasWidgets.length - 1" @click="moveWidget(index, 1)">下移</button></div>
              <WidgetCard :config="item" @remove="removeWidget(index)" @resize="onWidgetResize"
                @update="onWidgetUpdate" />
            </div>
          </div>
        </div>
      </main>
    </div>
  </div>
</template>

<script>
import WidgetCard from './common/WidgetCard.vue'
import { widgetList, getWidgetsByCategory } from './widgets/index.js'

const GAP = 12

export default {
  name: 'CustomComponents',
  components: { WidgetCard },
  data() {
    return {
      panelOpen: false,
      isMobile: window.innerWidth <= 768,
      nextId: 1,
      categorizedWidgets: getWidgetsByCategory(),
      collapsedCategories: {},
      canvasWidgets: [],
      layoutCache: {},
      masonryHeight: 0
    }
  },
  methods: {
    moveWidget(index, direction) {
      const target = index + direction
      if (target < 0 || target >= this.canvasWidgets.length) return
      const item = this.canvasWidgets.splice(index, 1)[0]
      this.canvasWidgets.splice(target, 0, item)
      this.$nextTick(() => this.recalculateLayout())
    },
    toggleCategory(category) {
      this.$set(this.collapsedCategories, category, !this.collapsedCategories[category])
    },
    addWidget(item) {
      const exists = this.canvasWidgets.some(w => w.widgetId === item.id)
      if (exists) {
        this.$msg.warning('该组件已添加')
        return
      }
      if (this.isMobile) this.panelOpen = false
      this.canvasWidgets.push({
        ...item,
        widgetId: item.id,
        id: this.nextId++,
        props: { ...(item.props || {}) }
      })
      this.$nextTick(() => {
        this.recalculateLayout()
        this.resyncObserver()
      })
    },
    removeWidget(index) {
      this.canvasWidgets.splice(index, 1)
      this.$nextTick(() => {
        this.recalculateLayout()
        this.resyncObserver()
      })
    },
    clearCanvas() {
      this.$confirm('确定清空所有组件?', '提示', { type: 'warning' })
        .then(() => {
          this.canvasWidgets = []
          this.layoutCache = {}
          this.masonryHeight = 0
          this.$nextTick(() => this.resyncObserver())
        })
        .catch(() => { })
    },
    saveLayout() {
      const layout = this.canvasWidgets.map(w => ({
        widgetId: w.widgetId || w.id,
        width: w.width,
        props: w.props
      }))
      localStorage.setItem('customLayout', JSON.stringify(layout))
      this.$msg.success('布局已保存')
    },
    loadLayout() {
      const saved = localStorage.getItem('customLayout')
      if (!saved) return
      try {
        const layout = JSON.parse(saved)
        this.canvasWidgets = layout.map(item => {
          const widget = widgetList.find(w => w.id === item.widgetId)
          return widget ? {
            ...widget,
            id: this.nextId++,
            widgetId: widget.id,
            width: item.width,
            props: { ...(widget.props || {}), ...(item.props || {}) }
          } : null
        }).filter(Boolean)
      } catch (e) {
        console.error('加载布局失败', e)
      }
    },
    dragStart(e, item) {
      e.dataTransfer.setData('widget', JSON.stringify(item))
    },
    handleDrop(e) {
      const data = e.dataTransfer.getData('widget')
      if (data) this.addWidget(JSON.parse(data))
    },
    onItemDrop(e, toIndex) {
      e.preventDefault()
      const fromId = e.dataTransfer.getData('text/plain')
      const fromIndex = this.canvasWidgets.findIndex(w => String(w.id) === fromId)
      if (fromIndex === -1 || fromIndex === toIndex) return
      const item = this.canvasWidgets.splice(fromIndex, 1)[0]
      this.canvasWidgets.splice(toIndex, 0, item)
      this.$nextTick(() => this.recalculateLayout())
    },
    onWidgetResize({ id, width }) {
      const widget = this.canvasWidgets.find(w => w.id === id)
      if (widget) {
        this.$set(widget, 'width', width)
        this.$nextTick(() => this.recalculateLayout())
      }
    },
    onWidgetUpdate({ id, data }) {
      console.log('Widget更新:', id, data)
    },
    // ========== 卡片尺寸监听 ==========
    initResizeObserver() {
      if (typeof ResizeObserver === 'undefined') return
      this._observed = new Set()
      this._resizeObserver = new ResizeObserver(() => this.scheduleRecalc())
      this.resyncObserver()
    },
    resyncObserver() {
      if (!this._resizeObserver || !this.$refs.masonry) return
      const current = new Set(Array.from(this.$refs.masonry.children))
      this._observed.forEach(el => {
        if (!current.has(el)) {
          this._resizeObserver.unobserve(el)
          this._observed.delete(el)
        }
      })
      current.forEach(el => {
        if (!this._observed.has(el)) {
          this._resizeObserver.observe(el)
          this._observed.add(el)
        }
      })
    },
    scheduleRecalc() {
      if (this._recalcTimer) clearTimeout(this._recalcTimer)
      this._recalcTimer = setTimeout(() => this.recalculateLayout(), 100)
    },
    // ========== 瀑布流布局 ==========
    getColumnCount(width) {
      if (this.isMobile || width < 500) return 1
      if (width < 900) return 2
      return 4
    },
    getSpan(width, cols) {
      if (cols <= 1) return 1
      return Math.min(Math.ceil(width / 100 * cols), cols)
    },
    recalculateLayout() {
      const container = this.$refs.masonry
      if (!container || this.canvasWidgets.length === 0) return

      const containerWidth = container.offsetWidth
      const cols = this.getColumnCount(containerWidth)
      const colWidth = (containerWidth - GAP * (cols - 1)) / cols
      const heights = new Array(cols).fill(0)
      const cache = {}

      this.canvasWidgets.forEach((item, i) => {
        const w = parseInt(item.width) || 50
        const span = this.getSpan(w, cols)

        // 找到最短的连续 span 列组
        let bestCol = 0
        let bestH = Infinity
        for (let c = 0; c <= cols - span; c++) {
          const maxH = Math.max(...heights.slice(c, c + span))
          if (maxH < bestH) { bestH = maxH; bestCol = c }
        }

        const itemWidth = span >= cols
          ? containerWidth
          : colWidth * span + GAP * (span - 1)

        cache[item.id] = {
          position: 'absolute',
          left: `${bestCol * (colWidth + GAP)}px`,
          top: `${bestH}px`,
          width: `${itemWidth}px`
        }

        // 读取已渲染 DOM 的实际高度来更新列高度
        const el = container.children[i]
        const h = el ? el.getBoundingClientRect().height : 150
        const newH = bestH + h + GAP
        for (let c = bestCol; c < bestCol + span; c++) {
          heights[c] = newH
        }
      })

      this.layoutCache = cache
      this.masonryHeight = Math.max(...heights, 0)
    }
  },
  created() {
    this.loadLayout()
  },
  mounted() {
    this.$nextTick(() => {
      this.recalculateLayout()
      this.initResizeObserver()
    })
    this._onResize = () => {
      this.isMobile = window.innerWidth <= 768
      if (!this.isMobile) this.panelOpen = false
      this.$nextTick(() => this.recalculateLayout())
    }
    window.addEventListener('resize', this._onResize)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this._onResize)
    if (this._resizeObserver) this._resizeObserver.disconnect()
    if (this._recalcTimer) clearTimeout(this._recalcTimer)
  }
}
</script>

<style scoped>
.custom-page {
  --c-primary: #1a73e8;
  --c-bg: #f5f7fa;
  --c-card: #ffffff;
  --c-text: #1a1a2e;
  --c-text-secondary: #6b7280;
  --c-border: #e5e7eb;
  height: 100%;
  background: var(--c-bg);
  font-family: 'Inter', -apple-system, sans-serif;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.page-content {
  display: flex;
  flex: 1;
  overflow: hidden;
  min-height: 0;
}

.component-panel {
  width: 230px;
  background: var(--c-card);
  border-right: 1px solid var(--c-border);
  display: flex;
  flex-direction: column;
}

.panel-header {
  padding: 16px;
  border-bottom: 1px solid var(--c-border);
}

.panel-title {
  font-size: 14px;
  font-weight: 600;
  color: var(--c-text);
  margin: 0;
}

.panel-body {
  flex: 1;
  overflow-y: auto;
  padding: 12px;
}

.panel-footer {
  padding: 12px;
  border-top: 1px solid var(--c-border);
  display: flex;
  gap: 8px;
}

.btn-action,
.btn-save {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 10px 12px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-action {
  background: none;
  border: 1px solid var(--c-border);
  color: var(--c-text-secondary);
}

.btn-action:hover {
  background: #fee2e2;
  border-color: #fca5a5;
  color: #ef4444;
}

.btn-save {
  background: var(--c-primary);
  border: none;
  color: #ffffff;
}

.btn-save:hover {
  opacity: 0.9;
}

.category-section {
  margin-bottom: 12px;
}

.category-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 6px 0;
  cursor: pointer;
  user-select: none;
}

.category-header:hover .category-title {
  color: var(--c-primary);
}

.category-title {
  font-size: 11px;
  font-weight: 700;
  color: var(--c-text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin: 0;
  transition: color 0.2s;
}

.category-arrow {
  font-size: 18px;
  color: var(--c-text-secondary);
  transition: transform 0.2s ease;
}

.category-arrow.collapsed {
  transform: rotate(-90deg);
}

.component-list {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.component-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  background: var(--c-bg);
  border: 1px solid transparent;
  border-radius: 8px;
  cursor: grab;
  transition: all 0.2s;
}

.component-item:hover {
  border-color: var(--c-primary);
  background: #f0f5ff;
}

.component-item .app-icon {
  font-size: 20px;
  color: var(--c-primary);
}

.component-name {
  font-size: 13px;
  font-weight: 500;
  color: var(--c-text);
}

.canvas-area {
  flex: 1;
  overflow-y: auto;
  padding: 20px;
  min-height: 0;
}

.canvas {
  min-height: 100%;
}

.canvas-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 60vh;
  color: var(--c-text-secondary);
}

.canvas-empty .app-icon {
  font-size: 64px;
  opacity: 0.3;
  margin-bottom: 16px;
}

.canvas-empty h3 {
  font-size: 18px;
  font-weight: 600;
  margin: 0 0 8px 0;
}

.canvas-empty p {
  font-size: 14px;
  margin: 0;
}

.masonry {
  position: relative;
}

.masonry-item {
  position: absolute;
  transition: left 0.3s ease, top 0.3s ease, width 0.3s ease;
}

@media (max-width: 768px) {
  .component-panel {
    width: 200px;
  }

  .component-item .component-name {
    font-size: 12px;
  }
}

.workspace-toolbar { flex-shrink:0; display:flex; justify-content:space-between; align-items:center; gap:16px; padding:24px; background:#f8fafc; }
.workspace-toolbar h2 { margin:0 0 6px; font-size:24px; color:#0f172a; }
.workspace-toolbar p { margin:0; color:#64748b; font-size:13px; line-height:1.6; }
.workspace-actions { display:flex; gap:8px; flex-shrink:0; }
.workspace-actions .btn-save, .workspace-actions .btn-action { padding:10px 14px; border-radius:10px; }
.workspace-actions .btn-save, .panel-close, .component-mask { display:none; }
.widget-order { display:flex; align-items:center; gap:8px; padding:10px 12px; background:#f1f5f9; border-radius:12px 12px 0 0; }
.widget-order span { flex:1; min-width:0; color:#475569; font-size:13px; }
.widget-order button, .panel-close { border:0; background:white; border-radius:6px; padding:8px; color:#2563eb; }
.widget-order button:disabled { opacity:.4; }
@media(max-width:768px) {
 .workspace-toolbar { flex-wrap:wrap; padding:18px 16px; }
 .workspace-toolbar h2 { font-size:22px; }
 .workspace-actions { width:100%; }
 .workspace-actions .btn-save { display:block; }
 .component-mask { display:block; position:fixed; inset:0; z-index:2100; background:rgba(15,23,42,.4); }
 .component-panel { position:fixed; inset:0 auto 0 0; width:min(320px, 88vw); z-index:2101; transform:translateX(-100%); transition:transform .2s; }
 .component-panel.is-open { transform:none; }
 .panel-close { display:block; margin-left:auto; font-size:22px; }
 .component-panel .panel-header { display:flex; align-items:center; }
 .custom-page .canvas-area { width:100%; min-width:0; padding:12px; }
 .component-item { min-height:44px; box-sizing:border-box; }
}
</style>



// WEBPACK FOOTER //
// src/components/User2/Custom.vue